import moment from 'moment'
import { useEffect, useMemo, useRef } from 'react'
import { fetchBookingDetailsByIds } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from '../reduxHooks'

export default function useTransferzOrderBookingDetails(orders: Array<App.Order>): boolean {
  const dispatch = useAppDispatch()
  const bookingDetails = useAppSelector(state => state.experience.bookingDetails)
  const bookingDetailsErrors = useAppSelector(state => state.experience.bookingDetailsErrors)
  const fetchingBookingDetails = useAppSelector(state => state.experience.fetchingBookingDetails)
  const attempts = useRef(new Map<string, number>())

  const missingDetails = useMemo(() => {
    const today = moment.utc().startOf('day')
    // Only dates adjacent to today can change tabs when evaluated in the pickup timezone.
    return orders.flatMap(order => order.transferItems
      .filter(item => item.provider === 'tfz' && item.status !== 'cancelled' && Math.abs(moment.utc(item.date).diff(today, 'days')) <= 1)
      .map(item => item.id))
      .filter(id => !bookingDetails[id] && !bookingDetailsErrors[id])
  }, [orders, bookingDetails, bookingDetailsErrors])

  useEffect(() => {
    // Retry an omitted booking once, then fall back rather than blocking the list.
    const toFetch = missingDetails.filter(id => !fetchingBookingDetails[id] && (attempts.current.get(id) ?? 0) < 2)
    if (toFetch.length) {
      toFetch.forEach(id => attempts.current.set(id, (attempts.current.get(id) ?? 0) + 1))
      dispatch(fetchBookingDetailsByIds(toFetch))
    }
  }, [dispatch, missingDetails, fetchingBookingDetails])

  return missingDetails.some(id => fetchingBookingDetails[id] || (attempts.current.get(id) ?? 0) < 2)
}
